import { Button, FormHelperText, Stack } from "@mui/material";
import React, { useCallback } from "react";
import Modal, { CommonModalProps } from "..";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { assetService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { immerable } from "immer";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import useEventListener from "../../../Utils/Hooks/useEventListener";
import { useTranslation } from "react-i18next";

export type ModalCreateFolderElementProps = {
  parentObjectId: number;
  resultObjects: DbAssetObjectInfos[];
  onConfirm?: (createdObject: DbAssetObjectInfos) => void;
};

export type ModalCreateFolderProps = CommonModalProps<ModalCreateFolderElementProps>;

const ModalCreateFolder = React.forwardRef<any, ModalCreateFolderProps>((props, ref) => {
  const { parentObjectId, closeModal, onConfirm, resultObjects, ...modalProps } = props;
  const { t } = useTranslation();
  const [name, setName] = React.useState<string>("");
  const enqueueCustomSnackbar = useCustomSnackbar();

  const isValid = useAppSelector(GetSectionIsValid("form-confirm", "sectionComment"));

  useEventListener("keydown", (e) => {
    if (e.key === "Enter" && isValid && !nameAlreadyExist) handleConfirmClick();
  });

  const nameAlreadyExist = React.useMemo(
    () => (resultObjects.find((x) => x.name === name) ? true : false),
    [name, resultObjects]
  );

  const updateInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleConfirmClick = useCallback(() => {
    let obj: DbAssetObjectInfos = new DbAssetObjectInfos({
      isDeleted: false,
      hidden: false,
      trash: false,
      platformId: 0,
      type: ObjectType.Folder,
      layer: ObjectLayer.Group,
      status: ObjectStatus.Undefined,
      name: name,
      parentObjectId: parentObjectId,
      providerId: 0,
      id: 0,
      isNew: false,
      [immerable]: false,
    });
    assetService
      .CreateStandalone({
        objs: [obj],
      })
      .then((response) => {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.folderCreated"), "success");
          onConfirm && response.createdObjects?.length === 1 && onConfirm(response.createdObjects[0]);
        } else enqueueCustomSnackbar(t("alert:error.creatingFolder", { message: response.message }), "error");
      });
    closeModal();
  }, [name]);

  const handleCancelClick = () => {
    closeModal();
  };

  return (
    <Modal
      title={t("modal:title.createNewFolder")}
      reference={ref}
      closeModal={handleCancelClick}
      width={504}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            color="success"
            variant="outlined"
            onClick={handleConfirmClick}
            disabled={!isValid || nameAlreadyExist}
          >
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={1}>
        <FormInputText
          autoFocus
          value={name}
          onChange={updateInputValue}
          placeholder={t("field:placeholder.typeFolderName")}
          rows={3}
          arya-aria-describedby="name-exist"
          validationProps={{
            formId: "form-confirm",
            formSectionId: "sectionComment",
            fieldId: "comment",
            initialState: false,
            required: true,
            minLength: 3,
          }}
        />
      </Stack>
      {nameAlreadyExist && (
        <FormHelperText error id="name-exist">
          {t("form:helper.nameAlreadyExist")}
        </FormHelperText>
      )}
    </Modal>
  );
});

export default ModalCreateFolder;
